import { JSX } from 'react'

import { Bot, LandmarkIcon, LucideIcon } from 'lucide-react'

import { SplitsAvatar } from '~/components/SplitsAvatar'
import { SupportedChainId } from '~/constants/chains'
import { useDisplayName } from '~/hooks/useDisplayName'
import { IAddress } from '~/types'

/**
 * Renders an icon inside a circular container matching the footprint of
 * SplitsAvatar, so icon-based accounts line up with regular avatars. The icon
 * is inset within the circle (ratio taken from the 36px → 20px reference).
 */
const IconAvatar = ({
  icon: Icon,
  size,
}: {
  icon: LucideIcon
  size: number
}): JSX.Element => {
  const box = `${size / 16}rem`
  const iconSize = `${Math.round((size * 5) / 9) / 16}rem`

  return (
    <div
      style={{ width: box, height: box }}
      className="flex flex-shrink-0 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700"
    >
      <Icon style={{ width: iconSize, height: iconSize }} />
    </div>
  )
}

/**
 * The avatar for any address: bank accounts and automations resolve to an
 * icon-avatar (Landmark / Bot), and every other address falls back to its
 * SplitsAvatar. `size` is the pixel footprint, matching SplitsAvatar.
 */
export const AddressDisplayAvatar = ({
  address,
  chainId,
  size = 24,
}: {
  address: IAddress
  chainId?: SupportedChainId
  size?: number
}): JSX.Element => {
  const { bankAccount, automation } = useDisplayName(address, chainId)

  if (bankAccount) return <IconAvatar icon={LandmarkIcon} size={size} />
  if (automation) return <IconAvatar icon={Bot} size={size} />

  return <SplitsAvatar size={size} address={address} />
}
